import { Box, Flex, Text } from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import { useTranslations } from '@ui/i18n';
import { formatNumber } from '@ui/utils/format';
import type { RewardChartPoint } from '@ui/views/Stake/useRewardChartData';
import useRewardChartData from '@ui/views/Stake/useRewardChartData';
import { colors, textStyles } from '@veno-app/chakra';
import type { Chart } from 'highcharts';
import type { MutableRefObject } from 'react';
import { Suspense, useEffect, useRef, useState } from 'react';

import { DynamicHighchartsReact } from './DynamicHighchartsReact';

export type MonthlyRewardChartProps = CardProps & {
  coinMode: CoinMode;
  amount: number;
  defaultPointIndex?: number;
  warningMessage?: string;
  errorMessage?: string;
  smallChart?: boolean;
};
export const MonthlyRewardChart = ({
  coinMode,
  amount,
  defaultPointIndex = 2,
  warningMessage,
  errorMessage,
  smallChart,
  ...props
}: MonthlyRewardChartProps) => {
  const [pointIndex, setPointIndex] = useState(defaultPointIndex);
  const [tooltipX, setTooltipX] = useState(0);
  const [tooltipY, setTooltipY] = useState(0);
  const [chartActive, setChartActive] = useState(false);
  const data = useRewardChartData(amount);

  const chartRef = useRef<Highcharts.Chart | null>(null);
  const chartData = data.map((data) => data.reward);
  const isEmptyData =
    Boolean(errorMessage || warningMessage) || isEmpty(chartData);

  const chartOptions = useChartOptions({
    data: chartData,
    pointIndex,
    isEmptyData,
    enablePlotLine: !isEmptyData,
    smallChart,
    chartRef,
    onPointIndexChanged: setPointIndex,
    onTooltipPositionChanged: ({ x, y }) => {
      setTooltipX(x);
      setTooltipY(y);
    },
  });

  return (
    <Suspense>
      <Box
        position="relative"
        mx={0}
        sx={{
          /**
           * disallow scrolling
           */
          touchAction: 'none',
          /**
           * don't truncate xAxis's labels
           */
          '.highcharts-axis-labels > span': {
            minW: '70px!important',
          },
          '.highcharts-axis-labels > span:first-of-type': {
            left: '10px!important',
          },
          '.highcharts-axis-labels > span:nth-of-type(2)': {
            transform: 'translateX(10px)!important',
          },
        }}
        onContextMenu={(e) => {
          // avoid showing context menu
          e.preventDefault();
          return false;
        }}
        isActive={!isEmptyData && chartActive && pointIndex >= 0}
        onTouchStartCapture={() => setChartActive(true)}
        onMouseDownCapture={() => setChartActive(true)}
        onTouchEnd={() => setChartActive(false)}
        onMouseEnter={() => setChartActive(true)}
        onMouseLeave={() => setChartActive(false)}
        {...props}
      >
        {isEmptyData ? (
          <Flex
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex="1"
            justify="center"
            pt="50px"
          >
            <Text
              textStyle="caption"
              color={errorMessage ? 'error' : 'warning'}
            >
              {errorMessage || warningMessage || 'Empty'}
            </Text>
          </Flex>
        ) : null}
        <Box
          hidden={isEmptyData}
          position="absolute"
          top={`${tooltipY}px`}
          left={`${tooltipX}px`}
          textStyle="caption"
          zIndex="1"
        >
          {tooltipFormatter(coinMode, data[pointIndex])}
        </Box>
        <DynamicHighchartsReact options={chartOptions} />
      </Box>
    </Suspense>
  );
};

const useChartOptions = ({
  data,
  pointIndex,
  onPointIndexChanged,
  onTooltipPositionChanged,
  smallChart,
  isEmptyData,
  enablePlotLine = true,
  chartRef,
}: {
  data: number[];
  pointIndex: number;
  onPointIndexChanged: (index: number) => void;
  onTooltipPositionChanged: (position: { x: number; y: number }) => void;
  isEmptyData: boolean;
  enablePlotLine?: boolean;
  smallChart?: boolean;
  chartRef: MutableRefObject<Chart | null>;
}): Highcharts.Options => {
  const t = useTranslations();

  useEffect(() => {
    const point = chartRef.current?.series[0].points[pointIndex] || null;

    if (!point) {
      return;
    }

    if (!enablePlotLine) {
      /**
       * hide plotLine and maker when no data
       */
      updatePlotLine(point, 'transparent');
      point.select(false, false);
    } else {
      /**
       * create a plotLine to replace crosshair, then we can
       * keep this plotLine always visible
       */
      updatePlotLine(point);
      /**
       * by selecting the point programmatically and customize
       * its `marker`'s styles in selected state, then we can keep
       * its `marker` always visible
       */
      point.select(true, false);

      // make sure tooltip has correct initial position
      chartRef.current?.tooltip?.refresh(point);
    }
  }, [chartRef, enablePlotLine, pointIndex]);

  const options: Highcharts.Options = {
    responsive: {
      rules: [
        {
          condition: {
            callback: () => !smallChart && window.innerWidth >= 768,
          },
          chartOptions: {
            chart: {
              height: 192,
            },
          },
        },
      ],
    },
    chart: {
      height: 140,
      animation: false,
      backgroundColor: 'transparent',
      events: {
        load() {
          chartRef.current = this;
        },
      },
      spacing: [18, 6, 16, 6],
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    plotOptions: {
      areaspline: {
        color: {
          // equals: linear-gradient(151.63deg, #FF96DF 31%, rgba(102, 211, 255, 0) 96%);
          linearGradient: { x1: 0, y1: 0, x2: 1, y2: 1 },
          stops: [
            [0.5, '#FF96DF'],
            [0.96, 'rgba(102, 211, 255, 0)'],
          ],
        },
        lineColor: 'rgba(0,0,0,0)',
        threshold: null,
        lineWidth: 1,
        marker: {
          lineColor: 'transparent',
          fillColor: 'transparent',
          states: {
            select: {
              lineColor: colors.bg.main,
              fillColor: '#FF96DF',
              lineWidth: 2,
              radius: 6,
            },
          },
        },
      },
      series: {
        pointPlacement: 'on',
        point: {
          events: {
            mouseOver() {
              onPointIndexChanged(this.index);
            },
          },
        },
      },
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      shadow: false,
      useHTML: true,
      padding: 0,
      style: {
        color: 'transparent',
        pointerEvents: 'none',
      },
      positioner(labelWidth, labelHeight, point) {
        const paddingX = 5;
        const pointX = this.chart.plotLeft + point.plotX;
        const position = {
          x:
            pointX - labelWidth < 0
              ? pointX + paddingX
              : pointX - 100 - paddingX,
          y: 10,
        };
        onTooltipPositionChanged(position);
        return position;
      },
    },
    yAxis: {
      visible: false,
      // fit the max and min tick to the available/full height
      endOnTick: false,
      startOnTick: false,
      min: isEmptyData ? 0 : data[0],
      max: isEmptyData ? 0.1 : data[data.length - 1],
    },
    xAxis: {
      categories: [
        t('{num} Months', {
          num: 1,
        }),
        t('{num} Months', {
          num: 6,
        }),
        t('{num} Months', {
          num: 12,
        }),
        t('{num} Months', {
          num: 24,
        }),
      ],
      lineWidth: 2,
      minPadding: 0,
      tickmarkPlacement: 'on',
      labels: {
        autoRotation: [0],
        padding: 0,
        style: {
          color: colors.text.light,
          ...textStyles.caption,
        },
        useHTML: true,
      },
    },
    series: [
      {
        type: 'areaspline',
        data: isEmptyData ? data.map(() => 0) : data,
      },
    ],
  };
  return options;
};

const updatePlotLine = (
  point: Highcharts.Point,
  lineColor: string = colors.pink[500],
) => {
  const chart = point.series.chart;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (chart.xAxis[0].plotLinesAndBands?.length > 0) {
    chart.xAxis[0].update({
      plotLines: [
        {
          id: 'xPlotLine',
          value: point.x,
          width: 1,
          color: lineColor,
        },
      ],
    });
  } else {
    chart.xAxis[0].addPlotLine({
      id: 'xPlotLine',
      value: point.x,
      width: 1,
      color: lineColor,
    });
  }
};

function isEmpty(data: number[] | null | undefined) {
  return !data || data.every((v) => v <= 0);
}

function tooltipFormatter(coinMode: CoinMode, dataPoint: RewardChartPoint) {
  const reward = dataPoint.reward;
  const displayDecimals = 2;
  let formattedValue = formatNumber(reward || 0, displayDecimals);
  const formattedUsd = formatNumber(dataPoint.usdReward, displayDecimals);

  if (Number(formattedValue) === 0 && reward !== 0) {
    const index = String(reward).search(/[1-9]/);
    formattedValue = String(reward).slice(0, index + displayDecimals);
  }
  return (
    <Flex
      rounded="5px"
      flexDir="column"
      alignItems="flex-end"
      minW="100px"
      boxShadow="card.normal"
      bg="bg.main"
      p={2}
    >
      <Text mb={2}>{dataPoint.date}</Text>
      <Text>
        {formattedValue} {COIN_MODE[coinMode].name}
      </Text>
      <Text color="text.light">{formattedUsd} USD</Text>
    </Flex>
  );
}
